import * as THREE from 'three'
import { MM_PER_UNIT, SectionFrame, Vec3 } from './types'

/**
 * The frame's orthonormal basis.
 *
 * `up` is only a hint: the caller gets it from a UI control or from world +Y, and neither
 * is guaranteed perpendicular to the surface normal. Gram-Schmidt removes the component
 * along the normal, so the section stays flat on the surface however the hint was given.
 * A hint parallel to the normal (a frame placed on the very top of a part, up = +Y) has
 * nothing left after the projection, so a fallback axis takes over.
 */
export function orthonormalise(
  normal: THREE.Vector3,
  upHint: THREE.Vector3
): { right: THREE.Vector3; up: THREE.Vector3; forward: THREE.Vector3 } {
  const forward = normal.clone().normalize()

  let up = upHint.clone().sub(forward.clone().multiplyScalar(upHint.dot(forward)))
  if (up.lengthSq() < 1e-8) {
    // The hint collapsed onto the normal. Any perpendicular will do; pick the world
    // axis the normal is least aligned with so the result is numerically stable.
    const alt = Math.abs(forward.y) < 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(0, 0, 1)
    up = alt.sub(forward.clone().multiplyScalar(alt.dot(forward)))
  }
  up.normalize()

  const right = new THREE.Vector3().crossVectors(up, forward).normalize()
  return { right, up, forward }
}

/** Frame → product-root matrix: columns are the basis, translation is the origin. */
export function frameMatrix(frame: SectionFrame): THREE.Matrix4 {
  const { right, up, forward } = orthonormalise(toVector3(frame.normal), toVector3(frame.up))
  const o = toVector3(frame.origin)
  return new THREE.Matrix4().makeBasis(right, up, forward).setPosition(o)
}

/**
 * Mesh-local → frame-space matrix, which is what the shader needs: it works in the mesh's
 * own `position` attribute, and the frame is authored against the product root.
 *
 * Composed rather than assumed: normalizeProduct bakes transforms into geometry and leaves
 * identity matrices behind, but a frame must not silently depend on that having happened.
 */
export function meshToFrameMatrix(
  frame: SectionFrame,
  mesh: THREE.Object3D,
  productRoot: THREE.Object3D
): THREE.Matrix4 {
  mesh.updateWorldMatrix(true, false)
  productRoot.updateWorldMatrix(true, false)

  const rootLocalFromMesh = new THREE.Matrix4().copy(productRoot.matrixWorld).invert().multiply(mesh.matrixWorld)

  return frameMatrix(frame).invert().multiply(rootLocalFromMesh)
}

/**
 * A frame for a fresh raycast hit.
 *
 * World +Y is the up hint because that is what reads as upright to someone looking at the
 * product — a section on a grip, a slide flat, or a bottle wall all want their artwork
 * the same way up, and only the surface they sit on differs.
 */
export function frameFromHit(
  point: THREE.Vector3,
  normal: THREE.Vector3,
  productRoot: THREE.Object3D,
  meshName: string,
  name: string,
  defaults: { width: number; height: number; depth: number; wrapRadius: number; maxAngleDeg: number }
): SectionFrame {
  productRoot.updateWorldMatrix(true, false)
  const inv = new THREE.Matrix4().copy(productRoot.matrixWorld).invert()

  const originLocal = point.clone().applyMatrix4(inv)
  const normalLocal = normal.clone().transformDirection(inv).normalize()
  const { up } = orthonormalise(normalLocal, new THREE.Vector3(0, 1, 0))

  return {
    id: `frame_${Math.random().toString(36).slice(2, 10)}`,
    name,
    meshName,
    origin: fromVector3(originLocal),
    normal: fromVector3(normalLocal),
    up: fromVector3(up),
    ...defaults,
  }
}

/**
 * Radius of the surface around the frame's up axis, in millimetres — the value that makes
 * `wrapRadius` reproduce a cylindrical wrap on a round part.
 *
 * Measured as the distance from the frame's origin to the mesh's bounding-box axis, taken
 * in the plane perpendicular to the frame's up. On a tumbler wall that is the tumbler's
 * radius; on a flat face it comes back large, which is the honest answer — a flat face
 * wants a flat decal, and the caller rounds a large radius down to 0.
 */
export function suggestWrapRadius(frame: SectionFrame, mesh: THREE.Object3D, productRoot: THREE.Object3D): number {
  const meshObj = mesh as THREE.Mesh
  if (!meshObj.geometry) return 0
  if (!meshObj.geometry.boundingBox) meshObj.geometry.computeBoundingBox()
  const bb = meshObj.geometry.boundingBox
  if (!bb) return 0

  // Bounding-box centre, carried from mesh-local into the product root's space.
  meshObj.updateWorldMatrix(true, false)
  productRoot.updateWorldMatrix(true, false)
  const rootFromMesh = new THREE.Matrix4().copy(productRoot.matrixWorld).invert().multiply(meshObj.matrixWorld)
  const centre = bb.getCenter(new THREE.Vector3()).applyMatrix4(rootFromMesh)

  const { up } = orthonormalise(toVector3(frame.normal), toVector3(frame.up))
  const toOrigin = toVector3(frame.origin).sub(centre)
  // Drop the component along the frame's up — the axis runs that way, so distance to it
  // is measured entirely in the perpendicular plane.
  toOrigin.sub(up.clone().multiplyScalar(toOrigin.dot(up)))

  return toOrigin.length() * MM_PER_UNIT
}

/** In-plane rotation of the frame, degrees, measured from the world-up-derived reference. */
export function frameRoll(frame: SectionFrame): number {
  const normal = toVector3(frame.normal)
  const reference = orthonormalise(normal, new THREE.Vector3(0, 1, 0))
  const current = orthonormalise(normal, toVector3(frame.up))
  const cos = THREE.MathUtils.clamp(current.up.dot(reference.up), -1, 1)
  const sin = current.up.dot(reference.right)
  return THREE.MathUtils.radToDeg(Math.atan2(sin, cos))
}

/** Rebuild `up` for a given in-plane rotation, keeping `normal` fixed. */
export function withRoll(frame: SectionFrame, degrees: number): SectionFrame {
  const normal = toVector3(frame.normal)
  const { right, up } = orthonormalise(normal, new THREE.Vector3(0, 1, 0))
  const rad = THREE.MathUtils.degToRad(degrees)
  const rolled = up
    .clone()
    .multiplyScalar(Math.cos(rad))
    .add(right.clone().multiplyScalar(Math.sin(rad)))
  return { ...frame, up: fromVector3(rolled.normalize()) }
}

export function toVector3(v: Vec3): THREE.Vector3 {
  return new THREE.Vector3(v[0], v[1], v[2])
}

export function fromVector3(v: THREE.Vector3): Vec3 {
  return [v.x, v.y, v.z]
}

/**
 * Where a point falls inside a frame — the CPU inverse of the shader's v3Project.
 *
 * Everything interactive needs this: which section the pointer is over, where in a section
 * a dragged element should land, whether a click is inside the engraved area at all. The
 * shader answers the same question per fragment, and the two answers have to agree or
 * artwork lands somewhere other than where it was dropped.
 *
 * So this is deliberately a line-for-line mirror of v3Project in projectorMaterial.ts. If
 * one changes, the other has to. They are in the same module for exactly that reason.
 *
 * `point` is in the PRODUCT ROOT's local space, the same space a frame's origin lives in.
 * Returns u/v in 0..1 across the section (outside that range means outside the section),
 * and `distance` in metres from the projected surface, which is what the depth throw is
 * tested against.
 */
export function frameUVFromPoint(
  frame: SectionFrame,
  point: THREE.Vector3
): { u: number; v: number; distance: number } {
  const p = point.clone().applyMatrix4(frameMatrix(frame).invert())

  const sizeX = Math.max(frame.width / MM_PER_UNIT, 1e-6)
  const sizeY = Math.max(frame.height / MM_PER_UNIT, 1e-6)
  const radius = frame.wrapRadius / MM_PER_UNIT

  let u: number
  let distance: number
  if (radius > 0) {
    // Arc length about the axis sitting `radius` behind the origin.
    const rx = p.x
    const rz = p.z + radius
    u = (Math.atan2(rx, rz) * radius) / sizeX + 0.5
    distance = Math.hypot(rx, rz) - radius
  } else {
    u = p.x / sizeX + 0.5
    distance = p.z
  }

  return { u, v: 0.5 - p.y / sizeY, distance }
}

/**
 * Is this point somewhere the section is actually engraved?
 *
 * Tests the same three things the shader does, in the same order: inside the rectangle,
 * within the projection throw, and on a surface that still faces the projector. The normal
 * is optional because some callers (a drag already known to be on the section) have no
 * face to offer; omitting it skips only the facing test.
 */
export function frameContains(frame: SectionFrame, point: THREE.Vector3, normal?: THREE.Vector3): boolean {
  const { u, v, distance } = frameUVFromPoint(frame, point)
  if (u < 0 || u > 1 || v < 0 || v > 1) return false
  if (Math.abs(distance) > frame.depth / MM_PER_UNIT / 2) return false
  if (!normal) return true

  const inv = frameMatrix(frame).invert()
  const n = normal.clone().transformDirection(inv).normalize()
  const p = point.clone().applyMatrix4(inv)
  const radius = frame.wrapRadius / MM_PER_UNIT

  const facing = radius > 0 ? n.dot(new THREE.Vector3(p.x, 0, p.z + radius).normalize()) : n.z
  return facing > Math.cos(THREE.MathUtils.degToRad(Math.min(frame.maxAngleDeg, 89)))
}

/**
 * Camera placement that looks squarely at a section.
 *
 * Replaces orbiting to a radial angle, which can only ever face a section whose normal
 * lies in the horizontal plane. Returns a world-space eye and target; the distance frames
 * the section's larger side with a little margin.
 */
export interface SafePoseOptions {
  /** World → the space the camera's position is expressed in: its parent's inverse. */
  toCameraParent?: THREE.Matrix4 | null
  /** Polar limits the camera controls enforce, radians from +Y. */
  minPolar?: number
  maxPolar?: number
  /** Azimuth to keep when the requested direction has none of its own. */
  fallbackAzimuth?: number
  /** Stay this far inside the polar limits, where yaw is better conditioned. */
  margin?: number
}

/**
 * A camera pose that looks at a point along a given direction, AND that an orbit
 * controller can actually hold.
 *
 * Both apps park a camera on a section's normal, and both hit the same two traps.
 *
 * SPACE. Orbit controllers write `camera.position`, which is parent-relative, and in both
 * apps the camera is a child of the scene. A world-space eye handed straight to setLookAt
 * is then read as a scene-space one, and lands somewhere else whenever that scene carries
 * a transform.
 *
 * LIMITS. Both apps clamp the polar angle (0.2π to 0.5π). A section facing near straight
 * up — the top of a slide, a lid — asks for a pose outside that. The controller clamps it,
 * so the camera is not where the caller thinks; and a near-vertical pose sits on the
 * singularity of the YXZ euler decomposition that viewers like to read back off the
 * camera, where yaw stops being well conditioned and ordinary damping jitter swings it.
 *
 * Pure maths, so it stays on the right side of this module's "three and nothing else"
 * rule — the caller owns its own controller.
 */
export function safeCameraPose(
  centreWorld: THREE.Vector3,
  normalWorld: THREE.Vector3,
  distance: number,
  options: SafePoseOptions = {}
): { eye: THREE.Vector3; target: THREE.Vector3 } {
  const target = centreWorld.clone()
  const direction = normalWorld.clone().normalize()

  if (options.toCameraParent) {
    target.applyMatrix4(options.toCameraParent)
    direction.transformDirection(options.toCameraParent).normalize()
  }

  const spherical = new THREE.Spherical().setFromVector3(direction)
  const margin = options.margin ?? 0.02
  const minPolar = (options.minPolar ?? 0) + margin
  const maxPolar = (options.maxPolar ?? Math.PI) - margin
  const polar = Math.min(Math.max(spherical.phi, minPolar), maxPolar)

  if (polar !== spherical.phi) {
    // Straight up or down has no meaningful azimuth — atan2(0, 0) is 0 — and snapping to
    // it turns the product for no reason the viewer can see. Keep the one they have.
    spherical.theta = options.fallbackAzimuth ?? spherical.theta
    spherical.phi = polar
  }

  spherical.radius = distance
  return { eye: target.clone().add(new THREE.Vector3().setFromSpherical(spherical)), target }
}

export function frameCameraPose(
  frame: SectionFrame,
  productRoot: THREE.Object3D,
  fovDeg: number,
  margin = 1.25,
  aspect = 1
): { eye: THREE.Vector3; target: THREE.Vector3 } {
  productRoot.updateWorldMatrix(true, false)

  const target = toVector3(frame.origin).applyMatrix4(productRoot.matrixWorld)
  const normal = toVector3(frame.normal).transformDirection(productRoot.matrixWorld).normalize()

  // Fit BOTH axes, against the camera's real shape.
  //
  // This used to take the larger of width and height and fit it to the vertical field of
  // view with a margin of 1.9 — which put a framed section at about a quarter of the view
  // height, so the thing being designed started small and the shopper had to zoom in before
  // doing anything. Worse on a phone: a portrait viewport's HORIZONTAL field of view is a
  // fraction of its vertical one, and that was not in the calculation at all, so a wide
  // section overflowed sideways while a tall one sat in a sea of empty space.
  //
  // Solving each axis separately and taking the greater distance fills whichever one runs
  // out first. The margin is then honest breathing room rather than a fudge: 1.25 leaves the
  // section at about 80% of the limiting axis.
  const halfFov = THREE.MathUtils.degToRad(fovDeg * 0.5)
  const forHeight = frame.height / MM_PER_UNIT / 2 / Math.tan(halfFov)
  const forWidth = frame.width / MM_PER_UNIT / 2 / Math.max(Math.tan(halfFov) * aspect, 1e-6)
  const distance = Math.max(forHeight, forWidth) * margin

  return { eye: target.clone().add(normal.multiplyScalar(distance)), target }
}
